import { useEffect } from "react";
import type { ResolvedPlace, Store } from "../../shared/types";
import { StoreDetails } from "./StoreDetails";

interface Props {
  /**
   * 表示対象の店舗。呼び出し側で `store !== null` のときだけマウントする前提
   * (App.tsx 参照) だが、後方互換のため nullable 型は残しておく。
   */
  store: Store;
  resolved: ResolvedPlace | null;
  onClose: () => void;
  /** 別 store への遷移ハンドラ。移転先ボタン等から呼ばれる。 */
  onNavigate: (storeId: string) => void;
  variant: "side" | "bottom";
}

/**
 * 呼び出し側 (App.tsx) で `store !== null` のときだけマウントする前提。
 * 以前は常時マウント + `translate-*` でスライドイン表示していたが、
 * 閉じている間も backdrop-blur + shadow のコストを払っていた (SP で顕著)。
 * 現在は conditional mount に倒し、transition を失う代わりに合成コストを削減。
 */
export function StoreSheet({ store, resolved, onClose, onNavigate, variant }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (variant === "side") {
    return (
      <aside
        role="dialog"
        aria-modal="false"
        className="absolute right-0 top-0 z-30 flex h-full w-[420px] flex-col border-l border-white/10 bg-slate-950/95 shadow-2xl shadow-black/50 backdrop-blur-xl"
      >
        <SheetHeader store={store} onClose={onClose} />
        <div className="thin-scroll flex-1 overflow-y-auto">
          <StoreDetails store={store} resolved={resolved} onNavigate={onNavigate} />
        </div>
      </aside>
    );
  }

  // SP: backdrop-blur を外して合成コストを下げる。
  // 背景は元から不透明寄り (bg-slate-950/98) なので blur 無しでも視認性十分。
  return (
    <>
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        className="fixed inset-0 z-30 bg-black/50"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-x-0 bottom-0 z-40 mx-auto max-h-[88dvh] overflow-hidden rounded-t-3xl border-t border-white/10 bg-slate-950 shadow-xl shadow-black/60 safe-bottom"
      >
        <div className="flex items-center justify-center pt-2.5">
          <div className="h-1.5 w-10 rounded-full bg-white/25" />
        </div>
        <SheetHeader store={store} onClose={onClose} />
        <div className="thin-scroll max-h-[calc(88dvh-72px)] overflow-y-auto">
          <StoreDetails store={store} resolved={resolved} onNavigate={onNavigate} />
        </div>
      </div>
    </>
  );
}

function SheetHeader({
  store,
  onClose,
}: {
  store: Store;
  onClose: () => void;
}) {
  // 移転先が閉店している場合は閉店扱いに倒す。
  // renewed は見た目 closed 寄りだが専用バッジ「リニューアル」。
  const isClosed = store.status === "closed" || store.movedToClosed === true;
  const isRenewed = store.status === "renewed";
  const isMoved = store.status === "moved" && !store.movedToClosed;
  const isInactive = isClosed || isRenewed;

  return (
    <div className="flex items-start justify-between gap-3 px-5 pb-2.5 pt-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2
            className={
              "truncate text-[19px] font-bold leading-tight " +
              (isInactive
                ? "text-slate-500 line-through decoration-slate-600"
                : "text-slate-50")
            }
          >
            {store.name}
          </h2>
          {isClosed && (
            <span className="shrink-0 rounded-full border border-slate-600/60 bg-slate-800/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              閉店
            </span>
          )}
          {isRenewed && (
            <span className="shrink-0 rounded-full border border-slate-600/60 bg-slate-800/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              リニューアル
            </span>
          )}
          {isMoved && (
            <span className="shrink-0 rounded-full border border-teal-400/40 bg-teal-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-200">
              移転
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-slate-400">
          {store.selectionCount}回選出
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="閉じる"
        className="shrink-0 rounded-full bg-white/5 p-2 text-slate-300 transition hover:bg-white/10 hover:text-slate-100"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M6 6l12 12M18 6l-12 12" />
        </svg>
      </button>
    </div>
  );
}
